import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  HostListener,
  ElementRef,
  ViewChild,
  Inject,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatBadgeModule } from '@angular/material/badge';
import {
  MatDialogModule,
  MatDialog,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { OverlayModule } from '@angular/cdk/overlay';
import { filterEducationalPathsForDisplay } from '../duna-user-progress-label/duna-user-progress-breakdown.util';

export interface EducationalPathBadgeInput {
  educationalPaths: any[];
  currentYear?: number;
  user?: any; // Add user object for profile modal
  showStatus?: boolean;
}

@Component({
  selector: 'app-duna-educational-path-badge',
  standalone: true,
  imports: [
    MatTooltipModule,
    MatIconModule,
    MatBadgeModule,
    MatDialogModule,
    OverlayModule,
  ],
  template: `
    <div
      class="educational-path-badge"
      [class.has-paths]="filteredAndSortedPaths.length > 0"
      [class.no-paths]="filteredAndSortedPaths.length === 0"
      (mouseenter)="onMouseEnter()"
      (mouseleave)="onMouseLeave()"
      (click)="onClick($event)"
      #badgeElement
    >
      <!-- Individual Path Badges -->
      @if (filteredAndSortedPaths.length > 0) {
      <div class="path-badges-container">
        @for (path of filteredAndSortedPaths; track path.path_id ?? $index) {
        <div
          class="individual-path-badge"
          [class]="
            getPathBadgeClass(
              path.current_year_status || path.validation_status,
              path
            )
          "
          [matTooltip]="getFullPathName(path)"
          matTooltipPosition="above"
        >
          <span class="path-name">{{ getShortPathName(path) }}</span>
        </div>
        }
      </div>
      }

      <!-- No Paths Display -->
      @if (filteredAndSortedPaths.length === 0) {
      <div class="no-paths-badge">
        <mat-icon class="path-icon">school</mat-icon>
        <span class="path-count">0</span>
      </div>
      }
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-educational-path-badge.component.scss'],
})
export class DunaEducationalPathBadgeComponent implements OnInit, OnChanges {
  @Input() educationalPaths: any[] = [];
  @Input() currentYear?: number;
  @Input() user?: any;
  @Input() showStatus = true;

  @ViewChild('badgeElement') badgeElement!: ElementRef;

  showTooltip = false;
  isMobile = false;
  filteredAndSortedPaths: any[] = [];

  /**
   * Determines if a provided label is effectively a placeholder and should not be shown
   */
  private isPlaceholderLabel(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    const v = String(value).trim().toLowerCase();
    if (v.length === 0) return true;

    // Common placeholder variants coming from backend or defaults
    const placeholders = new Set([
      'unknown path',
      'unknown',
      'unavngivet uddannelsessti',
      'unavngivet',
      'ukendt uddannelsessti',
      'ukendt',
      'unavngivet sti',
      'null', // Handle string "null" from backend
      'undefined', // Handle string "undefined" from backend
    ]);
    return placeholders.has(v);
  }

  /**
   * Picks the most appropriate display name, preferring a non-placeholder `name`,
   * then a non-placeholder `full_name`, and finally a hard fallback.
   * For badge display, prefer the name field when available (even if longer).
   */
  private getDisplayPathName(name?: string, fullName?: string): string {
    const candidateName = this.isPlaceholderLabel(name)
      ? ''
      : (name || '').trim();
    const candidateFullName = this.isPlaceholderLabel(fullName)
      ? ''
      : (fullName || '').trim();

    // Always prefer the name field when it's available and not a placeholder
    if (candidateName) {
      return candidateName;
    }

    // Fall back to full_name if name is not available
    return candidateFullName || fullName || name || 'Unknown Path';
  }

  private computeFilteredAndSortedPaths(): any[] {
    if (!this.educationalPaths || this.educationalPaths.length === 0) {
      return [];
    }

    const filteredPaths = filterEducationalPathsForDisplay(this.educationalPaths);

    return filteredPaths.sort((a, b) => a.path_id - b.path_id);
  }

  constructor(private dialog: MatDialog) {
    this.checkIfMobile();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['educationalPaths']) {
      this.filteredAndSortedPaths = this.computeFilteredAndSortedPaths();
    }
  }

  ngOnInit(): void {
    this.currentYear = this.currentYear || new Date().getFullYear();
    this.filteredAndSortedPaths = this.computeFilteredAndSortedPaths();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any): void {
    this.checkIfMobile();
  }

  private checkIfMobile(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  onMouseEnter(): void {
    if (!this.isMobile) {
      this.showTooltip = true;
    }
  }

  onMouseLeave(): void {
    if (!this.isMobile) {
      this.showTooltip = false;
    }
  }

  onClick(event: Event): void {
    event.stopPropagation();

    if (this.showStatus && this.user) {
      this.openProfileModal();
    } else if (this.isMobile) {
      this.openMobileDialog();
    } else {
      // Toggle tooltip on click for desktop as well
      this.showTooltip = !this.showTooltip;
    }
  }

  private openProfileModal(): void {
    void import('../duna-profile-user/duna-profile-user.component').then(
      ({ DunaProfileUserComponent }) => {
        this.dialog.open(DunaProfileUserComponent, {
          width: '95vw',
          maxWidth: '1200px',
          height: '90vh',
          data: {
            user: this.user,
            currentYear: this.currentYear,
          },
        });
      }
    );
  }

  private openMobileDialog(): void {
    const dialogRef = this.dialog.open(EducationalPathMobileDialogComponent, {
      width: '90vw',
      maxWidth: '400px',
      data: {
        educationalPaths: this.educationalPaths,
        currentYear: this.currentYear,
        showStatus: this.showStatus,
      },
    });
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'valid':
        return 'check_circle';
      case 'attention':
        return 'warning';
      case 'no_data':
        return 'help_outline';
      case 'no_paths':
        return 'cancel';
      default:
        return 'help_outline';
    }
  }

  getStatusIconClass(status: string): string {
    switch (status) {
      case 'valid':
        return 'status-valid';
      case 'attention':
        return 'status-attention';
      case 'no_data':
        return 'status-no-data';
      case 'no_paths':
        return 'status-no-paths';
      default:
        return 'status-no-data';
    }
  }

  getPathBadgeClass(status: string, path?: any): string {
    const baseClass = this.showStatus
      ? this.getStatusClass(status)
      : 'badge-neutral';
    const crossOrgClass =
      path && this.hasCrossOrgVersion(path) ? 'cross-org-indicator' : '';
    return `${baseClass} ${crossOrgClass}`;
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'valid':
        return 'badge-valid';
      case 'attention':
        return 'badge-attention';
      case 'no_data':
        return 'badge-no-data';
      case 'no_paths':
        return 'badge-no-paths';
      default:
        return 'badge-no-data';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'valid':
        return 'OK';
      case 'attention':
        return 'OBS';
      case 'no_data':
        return 'N/A';
      case 'no_paths':
        return 'INGEN';
      default:
        return 'N/A';
    }
  }

  /**
   * Hover text: the long name (`full_name`) when there is one, otherwise the short name.
   */
  getFullPathName(path: any): string {
    return this.getDisplayPathName(path?.full_name, path?.name);
  }

  /**
   * Badge label. The short name (`name`) always takes priority and is never abbreviated.
   * Only when a path has no short name is the long name used, abbreviated to 4 characters.
   */
  getShortPathName(path: any): string {
    if (!this.isPlaceholderLabel(path?.name)) {
      return String(path.name).trim().toUpperCase();
    }

    const displayName = this.getDisplayPathName(path?.name, path?.full_name);
    if (!displayName) {
      return '';
    }
    return displayName.substring(0, 4).toUpperCase();
  }

  hasCrossOrgVersion(path: any): boolean {
    // Check if user has a version assigned from a different organization
    // This would need to be passed from the backend in the path data
    return path.is_cross_organization_version === true;
  }
}

// Mobile Dialog Component
@Component({
  selector: 'app-educational-path-mobile-dialog',
  standalone: true,
  imports: [MatDialogModule, MatIconModule],
  template: `
    <div class="mobile-dialog">
      <div class="dialog-header">
        <h3>Uddannelsesveje ({{ data.educationalPaths.length }})</h3>
        <button mat-dialog-close class="close-button">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="dialog-content">
        @if (data.educationalPaths.length === 0) {
        <div class="no-paths-message">Ingen uddannelsesveje tildelt</div>
        } @for (path of data.educationalPaths; track path.path_id ?? $index) {
        <div class="path-item">
          <div class="path-name">
            @if (data.showStatus !== false) {
            <mat-icon
              class="status-icon"
              [class]="getStatusIconClass(path.validation_status)"
            >
              {{ getStatusIcon(path.validation_status) }}
            </mat-icon>
            }
            {{ getFullPathName(path) }}
          </div>
          @if (data.showStatus !== false) {
          <div class="path-status-info">
            <div class="status-row">
              <span class="status-label">Nuværende år:</span>
              <span
                class="status-value"
                [class]="
                  getStatusIconClass(
                    path.current_year_status || path.validation_status
                  )
                "
              >
                {{
                  getStatusText(
                    path.current_year_status || path.validation_status
                  )
                }}
              </span>
            </div>
            <div class="status-row">
              <span class="status-label">Sidste år:</span>
              <span
                class="status-value"
                [class]="
                  getStatusIconClass(
                    path.last_year_status || path.validation_status
                  )
                "
              >
                {{
                  getStatusText(path.last_year_status || path.validation_status)
                }}
              </span>
            </div>
          </div>
          } @if (data.showStatus !== false) {
          <div class="path-progress">
            {{ path.progress_percentage || 0 }}% færdig
          </div>
          }
        </div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .mobile-dialog {
        padding: 0;
      }

      .dialog-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-bottom: 1px solid #e0e0e0;
      }

      .dialog-header h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 500;
      }

      .close-button {
        background: none;
        border: none;
        cursor: pointer;
        padding: 4px;
      }

      .dialog-content {
        padding: 16px;
        max-height: 400px;
        overflow-y: auto;
      }

      .no-paths-message {
        text-align: center;
        color: #666;
        font-style: italic;
      }

      .path-item {
        padding: 12px 0;
        border-bottom: 1px solid #f0f0f0;
      }

      .path-item:last-child {
        border-bottom: none;
      }

      .path-name {
        display: flex;
        align-items: center;
        font-weight: 500;
        margin-bottom: 4px;
      }

      .status-icon {
        margin-right: 8px;
        font-size: 18px;
        width: 18px;
        height: 18px;
      }

      .status-valid {
        color: #4caf50;
      }

      .status-attention {
        color: #ff9800;
      }

      .status-no-data {
        color: #9e9e9e;
      }

      .status-no-paths {
        color: #f44336;
      }

      .path-progress {
        color: #666;
        font-size: 12px;
        margin-left: 26px;
      }

      .path-status-info {
        margin: 4px 0 4px 26px;
        font-size: 11px;
      }

      .status-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 2px 0;
      }

      .status-label {
        color: #666;
        font-weight: 500;
        font-size: 10px;
      }

      .status-value {
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 3px;
        font-size: 9px;
        text-transform: uppercase;

        &.status-valid {
          background-color: #e8f5e8;
          color: #2e7d32;
        }

        &.status-attention {
          background-color: #fff3e0;
          color: #f57c00;
        }

        &.status-no-data {
          background-color: #f5f5f5;
          color: #757575;
        }

        &.status-no-paths {
          background-color: #ffebee;
          color: #c62828;
        }
      }
    `,
  ],
})
export class EducationalPathMobileDialogComponent {
  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      educationalPaths: any[];
      currentYear: number;
      showStatus?: boolean;
    }
  ) {}

  /**
   * Determines if a provided label is effectively a placeholder and should not be shown
   */
  private isPlaceholderLabel(value: unknown): boolean {
    if (value === null || value === undefined) return true;
    const v = String(value).trim().toLowerCase();
    if (v.length === 0) return true;

    // Common placeholder variants coming from backend or defaults
    const placeholders = new Set([
      'unknown path',
      'unknown',
      'unavngivet uddannelsessti',
      'unavngivet',
      'ukendt uddannelsessti',
      'ukendt',
      'unavngivet sti',
      'null', // Handle string "null" from backend
      'undefined', // Handle string "undefined" from backend
    ]);
    return placeholders.has(v);
  }

  /**
   * Picks the most appropriate display name, preferring a non-placeholder `name`,
   * then a non-placeholder `full_name`, and finally a hard fallback.
   * For badge display, prefer the name field when available (even if longer).
   */
  private getDisplayPathName(name?: string, fullName?: string): string {
    const candidateName = this.isPlaceholderLabel(name)
      ? ''
      : (name || '').trim();
    const candidateFullName = this.isPlaceholderLabel(fullName)
      ? ''
      : (fullName || '').trim();

    // Always prefer the name field when it's available and not a placeholder
    if (candidateName) {
      return candidateName;
    }

    // Fall back to full_name if name is not available
    return candidateFullName || fullName || name || 'Unknown Path';
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'valid':
        return 'check_circle';
      case 'attention':
        return 'warning';
      case 'no_data':
        return 'help_outline';
      case 'no_paths':
        return 'cancel';
      default:
        return 'help_outline';
    }
  }

  getStatusIconClass(status: string): string {
    switch (status) {
      case 'valid':
        return 'status-valid';
      case 'attention':
        return 'status-attention';
      case 'no_data':
        return 'status-no-data';
      case 'no_paths':
        return 'status-no-paths';
      default:
        return 'status-no-data';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'valid':
        return 'OK';
      case 'attention':
        return 'OBS';
      case 'no_data':
        return 'N/A';
      case 'no_paths':
        return 'INGEN';
      default:
        return 'N/A';
    }
  }

  getFullPathName(path: any): string {
    // Use the display path name helper to handle placeholders properly
    return this.getDisplayPathName(path?.full_name, path?.name);
  }

  getShortPathName(path: any): string {
    // Use the display path name helper to handle placeholders properly
    const displayName = this.getDisplayPathName(path?.name, path?.full_name);

    // Handle null or undefined values
    if (!displayName) {
      return '';
    }

    // Return exactly 4 characters for badges
    return displayName.substring(0, 4).toUpperCase();
  }
}
